import { BranchesGame } from '../../games/branches/BranchesGame.js';
import { BranchesVortex } from '../../games/branches/BranchesVortex.js';
import { BranchesRenderer } from '../../games/branches/BranchesRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Branches-specific types ──────────────────────────────────────

export interface BranchesRenderOptions {
    // Placeholder — BranchesRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class BranchesTutorialAdapter implements TutorialGameAdapter {
    private game: BranchesGame;
    private renderer: BranchesRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new BranchesGame(grid);
        this.renderer = new BranchesRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
        // Tutorial scenes are hand-composed; keep them free of live hint rings.
        this.renderer.setHintRingsEnabled(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createBranchesTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new BranchesTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface BranchesVortexTutorialState {
    branches: string;
    gateMode?: boolean;
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class BranchesVortexTutorialAdapter implements TutorialGameAdapter {
    private game: BranchesVortex;
    private renderer: BranchesRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new BranchesVortex(grid);
        this.renderer = new BranchesRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.renderer.setHintRingsEnabled(false);
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as BranchesVortexTutorialState | null;
        this.game.reset();
        this.game.setVortexData(this.gatePairs, this.allGates);
        if (s?.branches) {
            this.game.deserialize(s.branches);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createBranchesVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex branches tutorial: D4 ↔ E2
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 3 }, { row: 2, col: 4 }],
    ];
    return new BranchesVortexTutorialAdapter(canvas, grid, gatePairs);
}
